import { format } from 'date-fns';

export function getSummaryResolution(summaryDurationUnit) {
    if (summaryDurationUnit === 'HOUR') return 'hour';
    return 'day';
}

export function parseTsToDate(value) {
    if (!value) return null;

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return new Date(value.getTime());
    }

    if (typeof value === 'number') {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date;
    }

    if (typeof value === 'string') {
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const date = new Date(normalized);

        if (!Number.isNaN(date.getTime())) return date;

        const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}))?(?::(\d{2}))?(?::(\d{2}))?/.exec(value);

        if (match) {
            const [, y, mo, d, h = '0', mi = '0', s = '0'] = match;
            const fallbackDate = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));

            if (!Number.isNaN(fallbackDate.getTime())) return fallbackDate;
        }
    }

    return null;
}

export function getTsCacheKey(ts) {
    if (!ts) return null;

    const date = parseTsToDate(ts);

    if (date) return date.toISOString();

    return String(ts);
}

export function toLocalDayDate(value) {
    const date = parseTsToDate(value);
    if (!date) return null;

    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getDateKey(value) {
    const date = parseTsToDate(value);
    if (!date) return '';

    return format(date, 'yyyy-MM-dd');
}

export function getHourKey(value) {
    const date = parseTsToDate(value);
    if (!date) return '';

    return format(date, 'HH');
}

export function getDateHourKey(value) {
    const date = parseTsToDate(value);
    if (!date) return '';

    return format(date, 'yyyy-MM-dd HH');
}

export function sortTimestampsDesc(timestamps) {
    if (!Array.isArray(timestamps)) return [];

    return [...timestamps].filter(Boolean).sort((a, b) => {
        const dateA = parseTsToDate(a);
        const dateB = parseTsToDate(b);

        const timeA = dateA ? dateA.getTime() : 0;
        const timeB = dateB ? dateB.getTime() : 0;

        return timeB - timeA;
    });
}

export function buildTimestampIndex(timestamps, resolution) {
    const sortedTimestamps = sortTimestampsDesc(timestamps);

    const allowedDateKeys = new Set();
    const dateKeyToLatestTsMap = {};
    const dateKeyToHourOptionsMap = {};

    sortedTimestamps.forEach((ts) => {
        const date = parseTsToDate(ts);

        if (!date) return;

        const dateKey = getDateKey(date);

        if (!dateKey) return;

        allowedDateKeys.add(dateKey);

        if (!dateKeyToLatestTsMap[dateKey]) {
            dateKeyToLatestTsMap[dateKey] = ts;
        }

        if (resolution === 'hour') {
            if (!dateKeyToHourOptionsMap[dateKey]) dateKeyToHourOptionsMap[dateKey] = [];

            dateKeyToHourOptionsMap[dateKey].push({
                key: getDateHourKey(date),
                hourKey: getHourKey(date),
                label: format(date, 'HH:mm'),
                ts,
                sortValue: date.getTime()
            });
        }
    });

    Object.keys(dateKeyToHourOptionsMap).forEach((dateKey) => {
        dateKeyToHourOptionsMap[dateKey] = dateKeyToHourOptionsMap[dateKey]
            .sort((a, b) => b.sortValue - a.sortValue)
            .map(({ sortValue, ...item }) => item);
    });

    return {
        sortedTimestamps,
        allowedDateKeys,
        dateKeyToLatestTsMap,
        dateKeyToHourOptionsMap
    };
}